export interface SubcategoryDefinition {
  name: string;
  apiKey: string;
  icon: string;
  popular?: boolean;
}

export interface MainCategoryDefinition {
  id: string;
  name: string;
  icon: string;
  subcategories: SubcategoryDefinition[];
}

interface CategoryMapping {
  category: string;
  icon: string;
}

const CATEGORY_MAP: Record<string, CategoryMapping> = {
  // Cleaning
  regular_cleaning: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  deep_cleaning: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  carpet_cleaning: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  window_washing: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  pressure_washing: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  pool_cleaning: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  laundry: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },
  biohazard_cleaning: { category: 'Cleaning', icon: 'assets/svg/brush.svg' },

  // Home Repair
  handyman: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  furniture_assembly: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  tv_mounting: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  drywall_repair: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  smart_home: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  grill_maintenance: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  roof_repair: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  gutter_cleaning: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  roof_inspection: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },
  inspection: { category: 'Home Repair', icon: 'assets/svg/hammer.svg' },

  // Landscaping
  lawn_mowing: { category: 'Landscaping', icon: 'assets/svg/leaf.svg' },
  tree_trimming: { category: 'Landscaping', icon: 'assets/svg/leaf.svg' },
  lawn_garden_care: { category: 'Landscaping', icon: 'assets/svg/leaf.svg' },
  leaf_removal: { category: 'Landscaping', icon: 'assets/svg/leaf.svg' },
  snow_removal: { category: 'Landscaping', icon: 'assets/svg/leaf.svg' },

  // Hauling
  local_moving: { category: 'Hauling', icon: 'assets/svg/car.svg' },
  packing_services: { category: 'Hauling', icon: 'assets/svg/car.svg' },
  furniture_moving: { category: 'Hauling', icon: 'assets/svg/car.svg' },
  junk_removal: { category: 'Hauling', icon: 'assets/svg/car.svg' },
  trash_service: { category: 'Hauling', icon: 'assets/svg/car.svg' },

  // Plumbing
  leak_repair: { category: 'Plumbing', icon: 'assets/svg/water.svg' },
  drain_cleaning: { category: 'Plumbing', icon: 'assets/svg/water.svg' },
  toilet_repair: { category: 'Plumbing', icon: 'assets/svg/water.svg' },
  faucet_install: { category: 'Plumbing', icon: 'assets/svg/water.svg' },
  water_heater: { category: 'Plumbing', icon: 'assets/svg/water.svg' },

  // Electrical
  wiring_outlets: { category: 'Electrical', icon: 'assets/svg/flash.svg' },
  lighting_install: { category: 'Electrical', icon: 'assets/svg/flash.svg' },
  ceiling_fan: { category: 'Electrical', icon: 'assets/svg/flash.svg' },
  panel_upgrade: { category: 'Electrical', icon: 'assets/svg/flash.svg' },

  // Painting
  interior_painting: { category: 'Painting', icon: 'assets/svg/color-palette.svg' },
  exterior_painting: { category: 'Painting', icon: 'assets/svg/color-palette.svg' },
  cabinet_painting: { category: 'Painting', icon: 'assets/svg/color-palette.svg' },
  deck_staining: { category: 'Painting', icon: 'assets/svg/color-palette.svg' },

  // HVAC
  ac_repair: { category: 'HVAC', icon: 'assets/svg/thermometer.svg' },
  heating_repair: { category: 'HVAC', icon: 'assets/svg/thermometer.svg' },
  duct_cleaning: { category: 'HVAC', icon: 'assets/svg/thermometer.svg' },
  thermostat_install: { category: 'HVAC', icon: 'assets/svg/thermometer.svg' },

  // Pest Control
  general_pest: { category: 'Pest Control', icon: 'assets/svg/bug.svg' },
  termite_treatment: { category: 'Pest Control', icon: 'assets/svg/bug.svg' },
  rodent_control: { category: 'Pest Control', icon: 'assets/svg/bug.svg' },
  mosquito_treatment: { category: 'Pest Control', icon: 'assets/svg/bug.svg' },
  pest_control: { category: 'Pest Control', icon: 'assets/svg/bug.svg' },

  // Locksmith
  lock_change: { category: 'Locksmith', icon: 'assets/svg/key.svg' },
  smart_lock: { category: 'Locksmith', icon: 'assets/svg/key.svg' },
  lockout_service: { category: 'Locksmith', icon: 'assets/svg/key.svg' },

  // Locksmith (parent-level key)
  locksmith: { category: 'Locksmith', icon: 'assets/svg/key.svg' },

  // Appliance Repair
  washer_dryer_repair: { category: 'Appliance Repair', icon: 'assets/svg/construct.svg' },
  refrigerator_repair: { category: 'Appliance Repair', icon: 'assets/svg/construct.svg' },
  dishwasher_repair: { category: 'Appliance Repair', icon: 'assets/svg/construct.svg' },
  oven_stove_repair: { category: 'Appliance Repair', icon: 'assets/svg/construct.svg' },
  appliance_repair: { category: 'Appliance Repair', icon: 'assets/svg/construct.svg' },

  // Parent-level keys returned by API
  painting: { category: 'Painting', icon: 'assets/svg/color-palette.svg' },
  plumbing: { category: 'Plumbing', icon: 'assets/svg/water.svg' },
};

/**
 * Build the category hierarchy dynamically from API service types.
 * Each service type becomes a subcategory grouped under a parent category
 * determined by the CATEGORY_MAP. Unmapped service types go under "Other".
 */
export function buildCategoryHierarchy(serviceTypes: any[]): MainCategoryDefinition[] {
  const categoryOrder: string[] = [];
  const categoryMap = new Map<string, MainCategoryDefinition>();

  const HIDDEN_KEYS = new Set(['other']);

  for (const st of serviceTypes) {
    if (HIDDEN_KEYS.has(st.key)) {continue;}

    const mapping = CATEGORY_MAP[st.key];
    const categoryName = mapping?.category ?? 'Other';
    const categoryIcon = mapping?.icon ?? 'assets/svg/construct.svg';
    const categoryId = categoryName.toLowerCase().replace(/\s+/g, '_');

    if (!categoryMap.has(categoryName)) {
      categoryMap.set(categoryName, {
        id: categoryId,
        name: categoryName,
        icon: categoryIcon,
        subcategories: []
      });
      categoryOrder.push(categoryName);
    }

    categoryMap.get(categoryName).subcategories.push({
      name: st.name === 'Regular Cleaning' ? 'House Cleaning' : st.name,
      apiKey: st.key,
      icon: st.icon_url || categoryIcon
    });

    if (st.key === 'regular_cleaning' && st.icon_url) {
      categoryMap.get(categoryName).icon = st.icon_url;
    }
  }

  return categoryOrder.map((name) => categoryMap.get(name));
}
